import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { makeStyles, CircularProgress } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import theme from "../../../themes";
import SuccessDialog from "../../common/SuccessDialog";
import ComingSoonDialog from "../../common/ComingSoonDialog";
import { useEnumStrings } from "../../enumStrings";
import {
  useGetAllCategoriesQuery,
  useGetAllDeletedDiscountsFromChamberIdQuery,
  useGetAllDiscountsFromChamberIdQuery,
  UserType,
} from "../../../graphql/types";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../common/Loader/Loader";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import { useLocation } from "react-router-dom";
import FullPageDiscountTable from "../../common/FullPageDiscountTable";
import { useGetOfferedToText } from "../../common/Hooks/useGetOfferedToText";
import { CSVLink } from "react-csv";
import ExportIcon from "../../../images/export.png";
import { getLocalTimestamp } from "../Root/RootAdmin";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import { openInstructionPdf } from "../../common/openInstructionPdf";

const useStyles = makeStyles((themes: any) => ({
  headerSection: {
    backgroundColor: theme.palette.primary.main,
    padding: "30px 40px",
    minHeight: "100px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    "@media (max-width: 768px)": {
      minHeight: "auto",
    },
    "@media (max-width: 600px)": {
      padding: "20px 16px",
    },
  },
  headerTitle: {
    fontSize: "32px",
    color: "white",
    fontWeight: "bold",
    margin: "0",
    "@media (max-width: 600px)": {
      fontSize: "22px",
    },
  },
  headerDescription: {
    fontSize: "18px",
    color: "rgba(255, 255, 255, 0.9)",
    margin: "0",
    lineHeight: "1.5",
    "@media (max-width: 600px)": {
      fontSize: "16px",
      lineHeight: "1.55",
    },
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "8px",
    "@media (max-width: 600px)": {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "8px",
      "& > div": {
        width: "100%",
      },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
  exportButton: {
    backgroundColor: "white",
    color: theme.palette.primary.main,
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "10px 20px",
    textTransform: "none",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    border: "none",
    cursor: "pointer",
    textDecoration: "none",
    whiteSpace: "nowrap",
    width: "fit-content",
    "&:hover": {
      backgroundColor: "white",
      color: theme.palette.primary.main,
    },
    "@media (max-width: 600px)": {
      width: "100%",
      minHeight: "44px",
    },
  },
  contentContainer: {
    backgroundColor: theme.palette.primary.main,
    padding: "0 40px 40px 40px",
    "@media (max-width: 600px)": {
      padding: "0 12px 24px 12px",
    },
  },
  content: {
    display: "flex",
    background: "#FFFFFF",
    minHeight: "70vh",
    borderRadius: "16px",
    padding: "30px",
    [theme.breakpoints.down("sm")]: {
      padding: "14px",
    },
    flexDirection: "column",
  },
}));

const ChamberAdminListAllDiscounts = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const getOfferedToText = useGetOfferedToText();
  const location = useLocation();
  const [createSuccessOpen, setCreateSuccessOpen] = useState(false);
  const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const bingo = useMemo(() => {
    return (location.state || { showToast: undefined, showSuccessDialog: undefined }) as {
      showToast: boolean | undefined;
      showSuccessDialog?: "created" | "deleted";
    };
  }, [location.state]);

  useEffect(() => {
    if (bingo.showSuccessDialog === "deleted") {
      setDeleteSuccessOpen(true);
      const timeoutId = setTimeout(() => setDeleteSuccessOpen(false), 5000);
      return () => clearTimeout(timeoutId);
    }
    if (bingo.showSuccessDialog === "created" || bingo.showToast) {
      setCreateSuccessOpen(true);
      const timeoutId = setTimeout(() => setCreateSuccessOpen(false), 5000);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [bingo.showSuccessDialog, bingo.showToast]);

  let chamberInfo = useGetChamberFromUserIdInAuth();
  let { data: discountData, loading: discountDataLoading } =
    useGetAllDiscountsFromChamberIdQuery({
      variables: { chamberId: chamberInfo?.id || "" },
      skip: !chamberInfo?.id,
      fetchPolicy: "cache-and-network",
    });

  let { data: deletedDiscountData, loading: deletedDiscountDataLoading } =
    useGetAllDeletedDiscountsFromChamberIdQuery({
      variables: { chamberId: chamberInfo?.id || "" },
      skip: !chamberInfo?.id,
      fetchPolicy: "cache-and-network",
    });

  const { data: categoriesData } = useGetAllCategoriesQuery();

  let exportPerksCsvDiscountListData = useMemo(() => {
    const nonDeletedDiscounts = discountData?.getAllDiscountsFromChamberId?.map(
      (item) => ({
        memberName: item.chamberMemberInfo?.name || "",
        discountName: item.title,
        offeredTo: getOfferedToText(item as any),
        discountViews: item.views || 0,
        creationTimeUtc: item.createdAtUtcMilli
          ? getLocalTimestamp(item.createdAtUtcMilli, true)
          : "",
        deletionTimeUtc: "",
        category1:
          categoriesData?.getAllCategories?.find(
            ({ id }) => id === item.chamberMemberInfo?.categoryId?.[0]
          )?.name || "",
        category2: item.chamberMemberInfo?.categoryId?.[1]
          ? categoriesData?.getAllCategories?.find(
            ({ id }) => id === item.chamberMemberInfo?.categoryId?.[1]
          )?.name || ""
          : "",
      })
    );

    const deletedDiscounts =
      deletedDiscountData?.getAllDeletedDiscountsFromChamberId?.map((item) => ({
        memberName: item.chamberMemberInfo?.name || "",
        discountName: item.title,
        offeredTo: getOfferedToText(item as any),
        discountViews: item.views || 0,
        creationTimeUtc: item.createdAtUtcMilli
          ? getLocalTimestamp(item.createdAtUtcMilli, true)
          : "",
        deletionTimeUtc: item.deletedAtUtcMilli
          ? getLocalTimestamp(item.deletedAtUtcMilli, true)
          : "",
        category1:
          categoriesData?.getAllCategories?.find(
            ({ id }) => id === item.chamberMemberInfo?.categoryId?.[0]
          )?.name || "",
        category2: item.chamberMemberInfo?.categoryId?.[1]
          ? categoriesData?.getAllCategories?.find(
            ({ id }) => id === item.chamberMemberInfo?.categoryId?.[1]
          )?.name || ""
          : "",
      }));

    const nonDeletedAndDeletedDiscounts = [
      ...(nonDeletedDiscounts || []),
      ...(deletedDiscounts || []),
    ];

    return [...nonDeletedAndDeletedDiscounts];
  }, [
    categoriesData?.getAllCategories,
    deletedDiscountData?.getAllDeletedDiscountsFromChamberId,
    discountData?.getAllDiscountsFromChamberId,
    getOfferedToText,
  ]);

  let exportPerksCsvDiscountListLabel = [
    {
      label: enumStrings.exportPerksCsvDiscountListLabel.memberName,
      key: "memberName",
    },
    {
      label: enumStrings.exportPerksCsvDiscountListLabel.category1,
      key: "category1",
    },
    {
      label: enumStrings.exportPerksCsvDiscountListLabel.category2,
      key: "category2",
    },
    {
      label: enumStrings.exportPerksCsvDiscountListLabel.discountName,
      key: "discountName",
    },
    {
      label: enumStrings.exportPerksCsvDiscountListLabel.offeredTo,
      key: "offeredTo",
    },
    {
      label: enumStrings.exportPerksCsvDiscountListLabel.discountViews,
      key: "discountViews",
    },
    {
      label: enumStrings.rootAdminText.creationTimeGmt,
      key: "creationTimeUtc",
    },
    {
      label: enumStrings.exportPerksCsvDiscountListLabel.deletionTimeUtc,
      key: "deletionTimeUtc",
    },
  ];

  const allDiscountData = useMemo(() => {
    return (
      discountData?.getAllDiscountsFromChamberId
        ?.slice()
        .sort((a, b) =>
          (a.chamberMemberInfo?.name || "") > (b.chamberMemberInfo?.name || "") ? 1 : -1
        ) || []
    ).map((discountMapData) => ({
      ...discountMapData,
      title: discountMapData.title?.trim() || "",
      chamberMemberInfo: {
        ...discountMapData.chamberMemberInfo,
        name: discountMapData.chamberMemberInfo?.name?.trim() || "",
      },
    }));
  }, [discountData?.getAllDiscountsFromChamberId]);

  return (
    <div style={{ overflowX: "hidden" }}>
      {/* Header Section */}
      <div className={classes.headerSection}>
        <h1 className={classes.headerTitle}>{enumStrings.listAllChamberDiscounts.title}</h1>
        <p className={classes.headerDescription}>
          Click on a member to update or delete their perk from the Chamber Perks App™. You can also see perk views and perk details.
        </p>
        <div className={classes.buttonContainer}>
          <VideoTutorialButton
            tutorialUrl="https://share.synthesia.io/embeds/videos/d6873d08-2e6f-497c-b9f2-1e9a97806ccb"
          />
          <ExportButton
            customOnClick={() => openInstructionPdf("Manage Member Perks.pdf")}
            icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
            label={enumStrings.manageChamberEngage?.viewInstructions || "VIEW INSTRUCTIONS"}
            disablePadding
          />
          <CSVLink
            data={exportPerksCsvDiscountListData}
            headers={exportPerksCsvDiscountListLabel}
            filename={`member_perks_export_${new Date().toISOString().slice(0, 10)}.csv`}
            className={classes.exportButton}
          >
            <img src={ExportIcon} alt="Export" style={{ width: "16px", height: "16px" }} />
            {discountDataLoading || deletedDiscountDataLoading ? (
              <CircularProgress size={14} style={{ color: theme.palette.primary.main }} />
            ) : (
              enumStrings.listAllChamberDiscounts.exportButton
            )}
          </CSVLink>
        </div>
      </div>

      {/* Content Container */}
      <div className={classes.contentContainer}>
        <div className={classes.content}>
          {!discountData || discountDataLoading ? (
            <Loader />
          ) : (
            <>
              {allDiscountData && allDiscountData.length > 0 ? (
                <FullPageDiscountTable
                  allDiscountData={allDiscountData}
                  userType={UserType.ChamberAdmin}
                  onDeleteSuccess={() => {
                    setDeleteSuccessOpen(true);
                    setTimeout(() => setDeleteSuccessOpen(false), 5000);
                  }}
                />
              ) : (
                <div
                  style={{
                    color: "black",
                    textAlign: "center",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: "auto",
                  }}
                >
                  {enumStrings.discountText.discountUnavailableText}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <SuccessDialog
        open={createSuccessOpen}
        onClose={() => setCreateSuccessOpen(false)}
        message={enumStrings.toastMessages.memberPerkCreatedSuccess}
      />
      <SuccessDialog
        open={deleteSuccessOpen}
        onClose={() => setDeleteSuccessOpen(false)}
        message={enumStrings.toastMessages.memberPerkDeletedSuccess}
      />

      <ComingSoonDialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
      />
    </div>
  );
};

export default ChamberAdminListAllDiscounts;
